import { ViewHook } from 'phoenix_live_view';

import { DropdownRef } from '../components/Dropdown';
import {
  LeafMentionSuggestion,
  MentionsList,
  MentionsListProps,
  MentionSuggestion,
} from '../components/mentions/MentionsList';
import { buildMockMentions } from '../components/mentions/mockMentions';
import ReactRenderer from '../react/renderer';
import MentionsProvider from '../services/mentions-provider';
import { sendError } from '../utils/errors';
import { buildPropsHelper, type PropsHelper } from './helpers/props';

const DEFAULT_MOCK_ROOT_ITEMS = 30;
const DEFAULT_MOCK_CHILD_ITEMS = 30;

const parsePositiveInt = (value: string | null, fallback: number): number => {
  if (!value) return fallback;
  const parsed = Number.parseInt(value, 10);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : fallback;
};

const getMockMentionsConfig = () => {
  if (typeof window === 'undefined') return null;

  const isLocalHost = ['localhost', '127.0.0.1'].includes(window.location.hostname);
  if (!isLocalHost) return null;

  const params = new URLSearchParams(window.location.search);
  const enabled = ['1', 'true', 'yes'].includes((params.get('mentions_mock') || '').toLowerCase());
  if (!enabled) return null;

  return {
    rootCount: parsePositiveInt(params.get('mentions_mock_roots'), DEFAULT_MOCK_ROOT_ITEMS),
    childCount: parsePositiveInt(params.get('mentions_mock_children'), DEFAULT_MOCK_CHILD_ITEMS),
  };
};

type MentionSelectHookProps = {
  eventName: string | undefined;
  eventTarget: string | undefined;
  wrapperId: string | undefined;
  mentionSuggestionsRaw: string | undefined;
};

type MentionSelectHook = Partial<ViewHook> &
  PropsHelper<MentionSelectHookProps> & {
    el: HTMLElement;
    props: MentionSelectProps;
    renderer: ReactRenderer<DropdownRef, MentionsListProps> | undefined;
    mentionsListWrapper: HTMLDivElement | undefined;
    closeGuardUntil: number;
    visibilityCheckRaf: number | undefined;
    mentionsProvider: MentionsProvider | undefined;
    input: HTMLInputElement | null;
    referenceElement: HTMLElement | null;

    onClickCallback: (e: MouseEvent) => void;
    parseMentionSuggestions: () => MentionSuggestion[];
    initialize: () => void;
    addEventListeners: () => void;
    removeEventListeners: () => void;
    markReferenceElementAsOpen: () => void;
    markReferenceElementAsClosed: () => void;
    isReferenceElementVisible: () => boolean;
    startVisibilityCheck: () => void;
    teardownRenderer: () => void;
    onClick: (e?: MouseEvent) => void;
  };

class MentionSelectProps {}

export default {
  MentionsSelect: {
    ...buildPropsHelper(),

    mounted() {
      this.onClickCallback = this.onClick.bind(this);
      this.closeGuardUntil = 0;
      this.visibilityCheckRaf = undefined;

      this.initialize();
    },

    updated() {
      if (this.propsChanged()) {
        this.removeEventListeners();
        this.initialize();
      }
    },

    destroyed() {
      this.removeEventListeners();
      this.teardownRenderer();

      if (this.mentionsListWrapper) {
        this.mentionsListWrapper.remove();
      }
    },

    getProps(): MentionSelectHookProps {
      return {
        eventName: this.el.dataset.eventName,
        eventTarget: this.el.dataset.eventTarget,
        wrapperId: this.el.dataset.wrapperId,
        mentionSuggestionsRaw: this.el.dataset.mentionSuggestions,
      };
    },

    parseMentionSuggestions() {
      const mockConfig = getMockMentionsConfig();
      if (mockConfig) {
        return buildMockMentions(mockConfig.rootCount, mockConfig.childCount);
      }

      if (!this.mentionSuggestionsRaw) return [];

      try {
        const parsed = JSON.parse(this.mentionSuggestionsRaw);
        return Array.isArray(parsed) ? parsed : [];
      } catch (e: any) {
        sendError(e, {
          action: 'MentionsSelect#parseMentionSuggestions',
          tags: {
            component: 'MentionsSelect',
          },
          params: {
            event_name: this.eventName,
            event_target: this.eventTarget,
            wrapper_id: this.wrapperId,
            mention_suggestions_length: this.mentionSuggestionsRaw?.length,
          },
          breadcrumb: {
            category: 'mentions',
            action: 'parse-suggestions-failed',
            metadata: {
              wrapper_id: this.wrapperId,
            },
          },
        });
        return [];
      }
    },

    initialize() {
      this.setProps();

      this.mentionsProvider = new MentionsProvider(this.parseMentionSuggestions());
      this.referenceElement = this.wrapperId ? document.getElementById(this.wrapperId) : this.el;

      if (this.renderer) {
        this.renderer.updateProps({
          referenceElement: this.referenceElement,
          mentionsProvider: this.mentionsProvider,
        });
      }

      this.addEventListeners();
    },

    addEventListeners() {
      this.el.addEventListener('click', this.onClickCallback);
    },

    removeEventListeners() {
      this.el.removeEventListener('click', this.onClickCallback);
    },

    markReferenceElementAsOpen() {
      this.referenceElement?.classList.add('--mentions-select-open');
    },

    markReferenceElementAsClosed() {
      this.referenceElement?.classList.remove('--mentions-select-open');
    },

    isReferenceElementVisible() {
      const referenceElement = this.referenceElement;
      if (!referenceElement || !referenceElement.isConnected) return false;

      const rect = referenceElement.getBoundingClientRect();
      return rect.width > 0 && rect.height > 0;
    },

    startVisibilityCheck() {
      if (this.visibilityCheckRaf != null) return;

      const check = () => {
        this.visibilityCheckRaf = undefined;
        if (!this.renderer) return;

        if (!this.isReferenceElementVisible()) {
          this.teardownRenderer();
          return;
        }

        this.visibilityCheckRaf = window.requestAnimationFrame(check);
      };

      this.visibilityCheckRaf = window.requestAnimationFrame(check);
    },

    teardownRenderer() {
      if (this.visibilityCheckRaf != null) {
        window.cancelAnimationFrame(this.visibilityCheckRaf);
        this.visibilityCheckRaf = undefined;
      }

      if (this.renderer) {
        const renderer = this.renderer;
        this.renderer = undefined;
        renderer.destroy();
      }

      this.markReferenceElementAsClosed();
    },

    onClick(e?: MouseEvent) {
      if (!this.mentionsProvider) return;

      e?.preventDefault();
      e?.stopPropagation();

      if (!this.mentionsListWrapper) {
        this.mentionsListWrapper = document.createElement('div');
      }
      const mentionsListWrapper = this.mentionsListWrapper;

      this.markReferenceElementAsOpen();
      this.closeGuardUntil = (window.performance?.now() ?? Date.now()) + 150;

      if (this.renderer) {
        this.teardownRenderer();
      }

      const mentionsProvider = this.mentionsProvider;
      const referenceElement = this.referenceElement;
      if (!mentionsProvider || !referenceElement || !mentionsListWrapper) return;

      setTimeout(() => {
        if (this.renderer) return;

        this.renderer = new ReactRenderer<DropdownRef, MentionsListProps>(MentionsList, {
          props: {
            referenceElement,
            mentionsProvider,
            handleKeyboard: true,
            onSelect: (suggestion: LeafMentionSuggestion) => {
              if (!this.eventName) return;

              const value =
                this.el.dataset.withLabels == 'true'
                  ? '{{{' + suggestion.label + '|' + suggestion.slug + '}}}'
                  : suggestion.slug;

              if (this.eventTarget) {
                this.pushEventTo!(this.eventTarget, this.eventName, { value });
              } else {
                this.pushEvent!(this.eventName, { value });
              }

              this.teardownRenderer();
            },
            onClose: () => {
              const now = window.performance?.now() ?? Date.now();
              if (now < this.closeGuardUntil) return;
              this.teardownRenderer();
            },
          },
          rootElement: mentionsListWrapper,
          renderOnInit: true,
        });

        this.startVisibilityCheck();
      }, 0);
    },
  } as MentionSelectHook,
};
